/**
 * Nomad — collection filter URLs (?nomad_tags=) + OR within group via client-side filter.
 */
(function (global) {
  const TAG_FILTER_GROUPS = {
    audience: ['gift-for-kids', 'gift-for-women', 'gift-for-men', 'gift-for-pastors'],
    occasion: ['gift-baptism', 'gift-wedding', 'gift-mothers-day', 'gift-easter', 'gift-christmas'],
    language: ['gift-english', 'gift-spanish'],
    material: ['bible-material-leather', 'bible-material-hardcover', 'bible-material-paperback'],
    'bible-language': ['bible-lang-english', 'bible-lang-spanish'],
  };

  const ALL_NOMAD_TAGS = new Set(Object.values(TAG_FILTER_GROUPS).flat());

  function isTagSegment(segment) {
    if (!segment) return false;
    return segment.split('+').some((t) => ALL_NOMAD_TAGS.has(t) || /^gift-/.test(t) || /^bible-/.test(t));
  }

  function getCollectionBaseFromPath(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts[0] !== 'collections' || parts.length < 2) return pathname.split('?')[0];
    return `/collections/${parts[1]}`;
  }

  function getCollectionHandleFromPath(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts[0] === 'collections' && parts.length >= 2) return parts[1];
    return '';
  }

  function getTagsFromPath(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length < 3 || !isTagSegment(parts[2])) return [];
    return parts[2].split('+').filter(Boolean);
  }

  function getTagsFromSearch(search) {
    const params = new URLSearchParams(search || '');
    const nomad = params.get('nomad_tags');
    if (nomad) {
      return nomad
        .split(',')
        .map((t) => decodeURIComponent(t.trim()))
        .filter(Boolean);
    }
    return [];
  }

  function getSelectedTags(path, search) {
    const fromQuery = getTagsFromSearch(search);
    if (fromQuery.length) return fromQuery;
    return getTagsFromPath(path || (typeof window !== 'undefined' ? window.location.pathname : ''));
  }

  function getPriceFromSearch(search) {
    const params = new URLSearchParams(search || '');
    return {
      gte: params.get('filter.v.price.gte') || '',
      lte: params.get('filter.v.price.lte') || '',
    };
  }

  function findGroupForTag(tag) {
    for (const [key, tags] of Object.entries(TAG_FILTER_GROUPS)) {
      if (tags.includes(tag)) return key;
    }
    return null;
  }

  function tagsByGroup(selectedTags) {
    const result = {};
    for (const [key, groupTags] of Object.entries(TAG_FILTER_GROUPS)) {
      const selected = selectedTags.filter((t) => groupTags.includes(t));
      if (selected.length) result[key] = selected;
    }
    return result;
  }

  /** Multiple tags in the same group need ?nomad_tags= + client OR filter. */
  function needsNomadClientFilter(selectedTags) {
    return Object.values(tagsByGroup(selectedTags)).some((groupSelected) => groupSelected.length > 1);
  }

  /** One tag per group → legacy path /handle/tag1+tag2 (server-side AND). */
  function canUsePathFilter(selectedTags) {
    return selectedTags.length > 0 && !needsNomadClientFilter(selectedTags);
  }

  function appendQueryParams(baseUrl, priceParams, preserveParams) {
    const params = new URLSearchParams();
    if (priceParams?.gte) params.set('filter.v.price.gte', priceParams.gte);
    if (priceParams?.lte) params.set('filter.v.price.lte', priceParams.lte);

    if (preserveParams !== false && typeof window !== 'undefined') {
      const current = new URLSearchParams(window.location.search);
      if (current.get('sort_by')) params.set('sort_by', current.get('sort_by'));
      if (current.get('preview_theme_id')) params.set('preview_theme_id', current.get('preview_theme_id'));
    }

    const qs = params.toString();
    if (!qs) return baseUrl;
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${qs}`;
  }

  function buildCollectionFilterUrl(collectionBase, tags, price, preserveParams, _path, search) {
    const uniqueTags = [...new Set((tags || []).filter(Boolean))];
    const basePath = (collectionBase || '').split('?')[0];
    const priceParams = price || getPriceFromSearch(search || (typeof window !== 'undefined' ? window.location.search : ''));

    if (canUsePathFilter(uniqueTags)) {
      const pathUrl = uniqueTags.length ? `${basePath}/${uniqueTags.join('+')}` : basePath;
      return appendQueryParams(pathUrl, priceParams, preserveParams);
    }

    const params = new URLSearchParams();
    if (uniqueTags.length) params.set('nomad_tags', uniqueTags.join(','));
    if (priceParams.gte) params.set('filter.v.price.gte', priceParams.gte);
    if (priceParams.lte) params.set('filter.v.price.lte', priceParams.lte);

    if (preserveParams !== false && typeof window !== 'undefined') {
      const current = new URLSearchParams(window.location.search);
      if (current.get('sort_by')) params.set('sort_by', current.get('sort_by'));
      if (current.get('preview_theme_id')) params.set('preview_theme_id', current.get('preview_theme_id'));
    }

    const qs = params.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }

  function toggleTagUrl(collectionBase, tag, path, search) {
    const tags = getSelectedTags(path, search);
    const idx = tags.indexOf(tag);
    if (idx >= 0) tags.splice(idx, 1);
    else tags.push(tag);
    return buildCollectionFilterUrl(collectionBase, tags, null, true, path, search);
  }

  function buildTagFilterUrl(collectionBase, groupKey, tag, path, search) {
    const groupTags = TAG_FILTER_GROUPS[groupKey] || [];
    const currentTags = getSelectedTags(path, search);
    const kept = currentTags.filter((t) => !groupTags.includes(t));
    if (tag) kept.push(tag);
    return buildCollectionFilterUrl(collectionBase, kept, null, true, path, search);
  }

  function buildPriceFilterUrl(collectionBase, gte, lte, path, search) {
    const tags = getSelectedTags(path, search);
    return buildCollectionFilterUrl(collectionBase, tags, { gte, lte }, true, path, search);
  }

  function removeTagUrl(collectionBase, tag, path, search) {
    const tags = getSelectedTags(path, search).filter((t) => t !== tag);
    return buildCollectionFilterUrl(collectionBase, tags, null, true, path, search);
  }

  function productMatchesTags(productTags, selectedTags) {
    if (!selectedTags.length) return true;
    const byGroup = tagsByGroup(selectedTags);
    const groups = Object.values(byGroup);
    if (!groups.length) return true;
    return groups.every((groupSelected) => groupSelected.some((t) => productTags.includes(t)));
  }

  function productMatchesPrice(cents, gte, lte) {
    const dollars = (parseInt(cents, 10) || 0) / 100;
    if (gte && dollars < parseFloat(gte)) return false;
    if (lte && dollars > parseFloat(lte)) return false;
    return true;
  }

  /** Only migrate path URLs that need OR within group (same-group multi-tag). */
  function migrateLegacyPathTags() {
    if (typeof window === 'undefined') return false;
    const pathTags = getTagsFromPath(window.location.pathname);
    if (!pathTags.length) return false;
    if (getTagsFromSearch(window.location.search).length) return false;
    if (!needsNomadClientFilter(pathTags)) return false;

    const base = getCollectionBaseFromPath(window.location.pathname);
    const url = buildCollectionFilterUrl(
      base,
      pathTags,
      getPriceFromSearch(window.location.search),
      true,
      window.location.pathname,
      window.location.search,
    );
    window.location.replace(url);
    return true;
  }

  global.NomadCollectionFilters = {
    TAG_FILTER_GROUPS,
    ALL_NOMAD_TAGS,
    isTagSegment,
    getCollectionBaseFromPath,
    getCollectionHandleFromPath,
    getTagsFromPath,
    getTagsFromSearch,
    getSelectedTags,
    getPriceFromSearch,
    buildCollectionFilterUrl,
    buildTagFilterUrl,
    buildPriceFilterUrl,
    toggleTagUrl,
    removeTagUrl,
    findGroupForTag,
    tagsByGroup,
    needsNomadClientFilter,
    canUsePathFilter,
    productMatchesTags,
    productMatchesPrice,
    migrateLegacyPathTags,
  };
})(typeof window !== 'undefined' ? window : globalThis);
